import { useContext } from "react";

import {
  FRAUDULENT_PAYMENT_INTENT,
  FormType,
  ORGANIZATION_CANNOT_ACCEPT_PAYMENT_WITHOUT_BANK_ERROR_CODE,
} from "@simplyk/common";
import { useElements, useStripe } from "@stripe/react-stripe-js";
import { PaymentMethod as StripePaymentMethod } from "@stripe/stripe-js";
import { inferRouterOutputs } from "@trpc/server";
import { UseFormSetValue } from "react-hook-form";
import { v4 } from "uuid";

import { PaymentErrorReport, unknownPaymentErrorCode } from "./helper";

import { isTest } from "@/common/constants/env";
import { RecaptchaAction } from "@/common/constants/recaptcha";
import { FrontendFormContext } from "@/common/contexts/FrontendFormContext";
import { getBillingDetails } from "@/common/helpers/payment";
import { getDeclineCodeFromServerError } from "@/common/helpers/stripe";
import { AppRouterType, trpc } from "@/common/helpers/trpc";
import { useRecaptchaRetry } from "@/common/hooks/useRecaptchaRetry";
import { useTranslate } from "@/common/hooks/useTranslate";
import { DonationFormPaymentInput } from "@/common/types/donationForm";
import { TicketingPaymentInput } from "@/common/types/ticketing";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { SubmitCommandFormInput } from "@/components/PaymentProcessor/helper";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { useSubmitCommand } from "@/components/PaymentProcessor/hooks/useSubmitCommand";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { isPaymentFailed } from "@/components/PaymentProcessor/type";

type SubmitCommandOutput = inferRouterOutputs<AppRouterType>["form_submitCommand"];

export type HandlePostSubmit = ({
  paymentInput,
  command,
  transaction,
  formType,
  isExpressCheckout,
  emailUpdateToken,
  buyerTicketToken,
}: {
  paymentInput: DonationFormPaymentInput | TicketingPaymentInput;
  command?: NonNullable<NonNullable<SubmitCommandOutput>["data"]>["command"];
  transaction: NonNullable<NonNullable<SubmitCommandOutput>["data"]>["transaction"];
  formType: FormType;
  isExpressCheckout?: boolean;
  emailUpdateToken?: string;
  buyerTicketToken?: string;
}) => Promise<{
  returnUrl?: string | null | undefined;
  error?: string | null | undefined;
}>;

export const useStripePayment = ({
  setValue,
  handlePaymentError,
  handlePostSubmit,
}: {
  setValue: UseFormSetValue<DonationFormPaymentInput | TicketingPaymentInput>;
  handlePaymentError: (report: PaymentErrorReport) => void;
  handlePostSubmit: HandlePostSubmit;
}) => {
  const { organization, commandId, formType } = useContext(FrontendFormContext);
  const { handleSubmitCommand } = useSubmitCommand({ setValue });
  const { t } = useTranslate();
  const stripe = useStripe();
  const elements = useElements();
  const { executeTrpc } = useRecaptchaRetry();

  const { mutateAsync: confirmPaymentIntentMutation } = trpc.form_confirmPaymentIntent.useMutation();

  // eslint-disable-next-line sonarjs/cognitive-complexity
  const payByStripe = async (submitCommandInput: SubmitCommandFormInput): Promise<string | undefined> => {
    const pm = submitCommandInput.paymentInput.paymentMethod;
    const express = Boolean(submitCommandInput.isExpressCheckout);

    if (!stripe || !elements) {
      handlePaymentError({
        rawErrorCode: "stripe_load_error",
        rawErrorMessage: t("common", "unknown_error", { code: unknownPaymentErrorCode.card1.code }),
        step: "stripe-load",
        paymentMethod: pm,
        isExpressCheckout: express,
        commandId,
        isKnownError: false,
      });
      return;
    }

    const res = await elements?.submit();

    if (res.error) {
      elements.getElement("payment")?.focus();
      handlePaymentError({
        rawErrorCode: res.error.code || "stripe_element_submit_error",
        rawErrorMessage:
          res.error.message || t("common", "unknown_error", { code: unknownPaymentErrorCode.card1.code }),
        step: "stripe-element-submit",
        paymentMethod: pm,
        isExpressCheckout: express,
        commandId,
        isKnownError: true,
      });
      return;
    }

    const billing_details = getBillingDetails(submitCommandInput.paymentInput, organization);

    const paymentMethodResult = await createPaymentMethod({
      stripe,
      elements,
      billing_details,
    });

    if (paymentMethodResult.error) {
      handlePaymentError({
        rawErrorCode: paymentMethodResult.error.code || "create_payment_method_error",
        rawErrorMessage:
          paymentMethodResult.error.message ||
          t("common", "unknown_error", { code: unknownPaymentErrorCode.card1.code }),
        step: "create-payment-method",
        paymentMethod: pm,
        isExpressCheckout: express,
        commandId,
        isKnownError: true,
      });
      return;
    }

    const paymentMethod = paymentMethodResult.paymentMethod;

    const submitResponse = await handleSubmitCommand({
      ...submitCommandInput,
      stripePaymentMethodId: paymentMethod.id,
      cardBrand: paymentMethod.card?.brand ?? null,
    });

    if (submitResponse && isPaymentFailed(submitResponse)) {
      handlePaymentError({
        rawErrorCode: submitResponse.error.code || "server_submit_error",
        rawErrorMessage:
          submitResponse.error.code === ORGANIZATION_CANNOT_ACCEPT_PAYMENT_WITHOUT_BANK_ERROR_CODE
            ? ORGANIZATION_CANNOT_ACCEPT_PAYMENT_WITHOUT_BANK_ERROR_CODE
            : submitResponse.error.message,
        step: "server-submit",
        paymentMethod: pm,
        isExpressCheckout: express,
        commandId,
        isKnownError: true,
        declineCode: submitResponse.error.declineCode,
      });
      return;
    }

    const clientSecret = submitResponse.data?.clientSecret;
    const command = submitResponse.data?.command;
    const transaction = submitResponse.data?.transaction ?? null;
    const emailUpdateToken = submitResponse.data?.emailUpdateToken;
    const buyerTicketToken = submitResponse.data?.buyerTicketToken;
    const { paymentInput, isExpressCheckout } = submitCommandInput;

    if (clientSecret === FRAUDULENT_PAYMENT_INTENT.clientSecret) {
      const result = await handlePostSubmit({
        command,
        transaction,
        formType,
        paymentInput,
        isExpressCheckout,
        emailUpdateToken,
        buyerTicketToken,
      });
      if (result.error || !result.returnUrl) {
        handlePaymentError({
          rawErrorCode: result.error ? "post_submit_error" : "missing_return_url",
          rawErrorMessage: t("common", "unknown_error", { code: unknownPaymentErrorCode.card2.code }),
          step: "post-submit",
          paymentMethod: pm,
          isExpressCheckout: express,
          commandId: command?.id,
          isKnownError: false,
        });
        return;
      }
      return result.returnUrl;
    }

    if (!command) {
      handlePaymentError({
        rawErrorCode: "missing_command",
        rawErrorMessage: t("common", "unknown_error", { code: unknownPaymentErrorCode.card3.code }),
        step: "server-submit",
        paymentMethod: pm,
        isExpressCheckout: express,
        commandId,
        isKnownError: false,
      });
      return;
    }

    if (clientSecret && !isTest) {
      try {
        const { error, paymentIntent, setupIntent } = await stripe.handleNextAction({ clientSecret });
        if (error) {
          handlePaymentError({
            rawErrorCode: error.code || "handle_next_action_error",
            rawErrorMessage:
              error.message || t("common", "unknown_error", { code: unknownPaymentErrorCode.card4.code }),
            step: "handle-next-action",
            paymentMethod: pm,
            isExpressCheckout: express,
            commandId: command.id,
            isKnownError: true,
            declineCode: error.decline_code,
          });
          return;
        }

        if (!paymentIntent && !setupIntent) {
          handlePaymentError({
            rawErrorCode: "missing_intent",
            rawErrorMessage: t("common", "unknown_error", { code: unknownPaymentErrorCode.card4.code }),
            step: "handle-next-action",
            paymentMethod: pm,
            isExpressCheckout: express,
            commandId: command.id,
            isKnownError: false,
          });
          return;
        }

        if (paymentIntent && paymentIntent.status === "requires_payment_method") {
          handlePaymentError({
            rawErrorCode: paymentIntent.last_payment_error?.code || "requires_payment_method",
            rawErrorMessage:
              paymentIntent.last_payment_error?.message ||
              t("common", "unknown_error", { code: unknownPaymentErrorCode.card5.code }),
            step: "handle-next-action",
            paymentMethod: pm,
            isExpressCheckout: express,
            commandId: command.id,
            isKnownError: Boolean(paymentIntent.last_payment_error),
            declineCode: paymentIntent.last_payment_error?.decline_code,
          });
          return;
        }

        if (paymentIntent && paymentIntent.status === "requires_confirmation") {
          const action = RecaptchaAction.ConfirmPaymentIntent;
          const email = command.email;
          const { data, error } = await executeTrpc(action, email, confirmPaymentIntentMutation, {
            paymentIntentId: paymentIntent.id,
          });

          if (error || !data) {
            const code = error?.code || "unknown_error";

            const unknownGenericErrorTraduction = t("common", "unknown_error", {
              code: unknownPaymentErrorCode.card6.code,
            });

            const displayedMessage =
              code === "confirm_payment_intent_failed"
                ? error?.message
                : t("common", `error.${code as never}`) === code
                  ? unknownGenericErrorTraduction
                  : t("common", `error.${code as never}`);

            handlePaymentError({
              rawErrorCode: code,
              rawErrorMessage: displayedMessage || unknownGenericErrorTraduction,
              step: "manual-confirmation",
              paymentMethod: pm,
              isExpressCheckout: express,
              commandId: command.id,
              isKnownError: code === "confirm_payment_intent_failed",
              declineCode: getDeclineCodeFromServerError(error),
            });
            return;
          }
        }
      } catch (error) {
        const caughtMessage = error instanceof Error ? error.message : String(error);
        handlePaymentError({
          rawErrorCode: `caught_exception  – ${caughtMessage}`,
          rawErrorMessage: t("common", "unknown_error", { code: unknownPaymentErrorCode.card7.code }),
          step: "handle-next-action",
          paymentMethod: pm,
          isExpressCheckout: express,
          commandId: command.id,
          isKnownError: false,
        });
        return;
      }
    }

    const result = await handlePostSubmit({
      paymentInput,
      command,
      transaction,
      formType,
      isExpressCheckout,
      emailUpdateToken,
      buyerTicketToken,
    });

    if (result.returnUrl) {
      return result.returnUrl;
    }

    if (result.error) {
      handlePaymentError({
        rawErrorCode: "post_submit_error",
        rawErrorMessage: result.error,
        step: "post-submit",
        paymentMethod: pm,
        isExpressCheckout: express,
        commandId: command.id,
        isPaymentSucceeded: true,
        isKnownError: false,
      });
    } else {
      handlePaymentError({
        rawErrorCode: "missing_return_url",
        rawErrorMessage: t("common", "unknown_error.paymentSucceeded", { code: unknownPaymentErrorCode.card8.code }),
        step: "post-submit",
        paymentMethod: pm,
        isExpressCheckout: express,
        commandId: command.id,
        isPaymentSucceeded: true,
        isKnownError: false,
      });
    }
  };

  return { payByStripe };
};

type CreatePaymentMethodParams = {
  stripe: ReturnType<typeof useStripe>;
  elements: ReturnType<typeof useElements>;
  billing_details: ReturnType<typeof getBillingDetails>;
};

type CreatePaymentMethodResult =
  | { paymentMethod: StripePaymentMethod; error?: undefined }
  | { paymentMethod?: undefined; error: { message?: string; code?: string } };

const createPaymentMethod = async ({
  stripe,
  elements,
  billing_details,
}: CreatePaymentMethodParams): Promise<CreatePaymentMethodResult> => {
  if (isTest) {
    return {
      paymentMethod: {
        id: `pm_test_${v4()}`,
      } as StripePaymentMethod,
    };
  }

  const { error, paymentMethod } = await stripe!.createPaymentMethod({
    elements: elements!,
    params: {
      billing_details,
    },
  });

  if (error) {
    return { error };
  }

  return { paymentMethod };
};
